import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { AlertTriangle, Check } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'
import { ConfidenceMeter } from '@/components/ui/ConfidenceMeter'
import { formatWilson } from '@/lib/utils'
import type { ConfidenceBreakdown, SegmentAggregate } from '@/lib/types'

const DEMO_CONFIDENCE: ConfidenceBreakdown = {
  score: 0.74,
  band: 'High',
  wilson: [0.72, 0.86],
  drivers: {
    evidence_volume: 0.85, evidence_relevance: 0.72, segment_coverage: 0.78,
    open_ended_depth: 0.68, pass_agreement: 0.86, productive_diversity: 0.4,
    prediction_diversity: 0.14, recency: 0.75, validation_history: 0.20, source_diversity: 0.70,
  },
  weak_signals: [],
}

interface PreRead {
  pre_read_id: string
  research_question: string
  stimuli: string
  result: string
  simulated_only: number
  created_at: number
}

interface Project {
  project_id: string
  name: string
}

export function ReportPage() {
  const { projectId } = useParams<{ projectId: string }>()
  const isDemo = !projectId || projectId === 'demo'

  const [copied, setCopied] = useState(false)
  const [project, setProject] = useState<Project | null>(null)
  const [latestPreRead, setLatestPreRead] = useState<PreRead | null>(null)
  const [aggregates, setAggregates] = useState<SegmentAggregate[]>([])
  const [loading, setLoading] = useState(!isDemo)

  useEffect(() => {
    if (isDemo) return
    const token = localStorage.getItem('echo_session')
    if (!token) { setLoading(false); return }

    Promise.all([
      fetch(`/api/projects/${projectId}`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.ok ? r.json() as Promise<Project> : null).catch(() => null),
      fetch(`/api/projects/${projectId}/pre-reads`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.ok ? r.json() as Promise<PreRead[]> : []).catch(() => []),
    ]).then(([proj, preReads]) => {
      if (proj) setProject(proj)
      const rows = Array.isArray(preReads) ? preReads : []
      const latest = rows[0] ?? null
      if (latest) {
        setLatestPreRead(latest)
        try { setAggregates(JSON.parse(latest.result) as SegmentAggregate[]) } catch (e) { console.error('Failed to parse pre-read result JSON', e) }
      }
      setLoading(false)
    }).catch(() => setLoading(false))
  }, [projectId, isDemo])

  // Build exec summary text for real projects
  const buildRealExecSummary = () => {
    if (!latestPreRead) return ''
    const topSegs = aggregates.slice(0, 3).map(a => a.segment_name).join(', ')
    return `EXECUTIVE SUMMARY\n${project?.name ?? projectId} — Echosome Echo Pre-Read\n\nResearch Question: ${latestPreRead.research_question}\n\nTop segments analysed: ${topSegs || 'N/A'}\n\nThis is a simulated pre-read result. See full report for segment-level confidence and recommendations.\n\nGenerated: ${new Date().toLocaleDateString()}`
  }

  const copyExecSummary = () => {
    const text = isDemo
      ? `EXECUTIVE SUMMARY\nInsurance Renewal Message Test — Echosome Echo Pre-Read\n\nDecision Recommendation: Proceed with Message B as primary renewal message.\n\nPredicted Winner: Message B — "explains the actual cost of claims paid in your area last year."\n\nConfidence: Medium-high overall (Wilson 72–86%). Low for Recent Claimants (Wilson 32–58%) — human validation recommended for this segment.\n\nWhy It Wins: Message B is the only option that gives a concrete explanation for premium increase. Evidence from 1,248 respondents and 362 open-ended answers consistently shows that "market conditions" language (Message A) erodes trust, while explicit cost explanation drives renewal intent. Message C's loyalty framing tests as hollow without a real explanation.\n\nValidation Plan: Validate Message B with 40 General Homeowners and 20 Recent Claimants before finalizing. Recent claimants need claims-acknowledgment language the echo may have underweighted.`
      : buildRealExecSummary()
    navigator.clipboard.writeText(text)
      .then(() => {
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
      })
      .catch(() => {
        // Clipboard access denied (non-HTTPS, browser permission denied, etc.)
        // Do not set copied=true — the copy did not succeed.
      })
  }

  const projectName = project?.name ?? (isDemo ? 'Insurance Renewal Message Test' : projectId ?? 'Project')
  const validationPath = `/app/projects/${isDemo ? 'demo' : projectId}/validation`
  const dataPath = `/app/projects/${isDemo ? 'demo' : projectId}/data`
  const preReadPath = `/app/projects/${isDemo ? 'demo' : projectId}/pre-read`

  // ── Loading ──────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="p-6 max-w-xl mx-auto pb-20 md:pb-6">
        <h1 className="text-2xl font-medium text-deep-ink mb-6">Client-Ready Report</h1>
        <Card className="text-center py-10">
          <p className="text-sm text-slate-400">Loading report data…</p>
        </Card>
      </div>
    )
  }

  // ── Real project: no pre-reads yet ───────────────────────────────────────────
  if (!isDemo && !latestPreRead) {
    return (
      <div className="p-6 space-y-6 max-w-3xl pb-20 md:pb-6">
        <h1 className="text-2xl font-medium text-deep-ink">Client-Ready Report</h1>
        <Card className="text-center py-10 space-y-3">
          <p className="text-sm font-medium text-deep-ink">No pre-read results yet</p>
          <p className="text-xs text-slate-500 max-w-xs mx-auto">
            Run an ensemble pre-read first. The report is generated from its segment predictions and confidence scores.
          </p>
          <div className="flex gap-3 justify-center pt-2">
            <Link to={preReadPath}>
              <Button variant="primary" size="sm">Run a pre-read →</Button>
            </Link>
          </div>
        </Card>
      </div>
    )
  }

  // ── Real project: has pre-read results ──────────────────────────────────────
  if (!isDemo && latestPreRead) {
    return (
      <div className="p-6 space-y-6 max-w-4xl pb-20 md:pb-6">
        <div className="flex items-center justify-between flex-wrap gap-3 no-print">
          <div>
            <h1 className="text-2xl font-medium text-deep-ink" style={{ letterSpacing: '-0.02em' }}>Client-Ready Report</h1>
            <p className="text-sm text-slate-500 mt-1">
              {projectName} · Generated {new Date().toLocaleDateString()}
              {latestPreRead.simulated_only ? (
                <span className="ml-2"><Badge variant="demo" size="sm">Simulated run</Badge></span>
              ) : (
                <span className="ml-2"><Badge variant="success" size="sm">Live LLM</Badge></span>
              )}
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={copyExecSummary}>
              {copied ? <><Check className="w-3.5 h-3.5 inline mr-1" />Copied</> : 'Copy summary'}
            </Button>
            <Link to={dataPath}>
              <Button variant="ghost" size="sm">View data</Button>
            </Link>
          </div>
        </div>

        {/* Research question */}
        <Card className="bg-off-white border-0">
          <p className="text-xs text-slate-400 uppercase tracking-wide mb-1">Research question</p>
          <p className="text-sm font-medium text-deep-ink">"{latestPreRead.research_question}"</p>
          <p className="text-xs text-slate-400 mt-2">
            {new Date(latestPreRead.created_at).toLocaleDateString()} · {aggregates.length} segment{aggregates.length !== 1 ? 's' : ''}
          </p>
        </Card>

        {/* Segment predictions */}
        {aggregates.length > 0 ? (
          <Card>
            <p className="text-xs text-slate-400 uppercase tracking-wide mb-3">Segment Predictions</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border-gray text-xs text-slate-400 text-left">
                    <th className="pb-2 pr-4 font-medium">Segment</th>
                    <th className="pb-2 pr-4 font-medium">Predicted winner</th>
                    <th className="pb-2 pr-4 font-medium">Confidence</th>
                    <th className="pb-2 pr-4 font-medium">Predicted share</th>
                    <th className="pb-2 font-medium"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border-gray">
                  {aggregates.map(agg => {
                    const conf = agg.confidence
                    const isGap = !agg.predicted_winner || conf?.band === 'Low'
                    return (
                      <tr key={agg.segment_id}>
                        <td className="py-2.5 pr-4 font-medium">{agg.segment_name}</td>
                        <td className="py-2.5 pr-4 text-human-teal">{agg.predicted_winner ?? '—'}</td>
                        <td className="py-2.5 pr-4">
                          {conf ? (
                            <Badge variant={conf.band === 'High' ? 'success' : conf.band === 'Medium' ? 'warning' : 'danger'} size="sm">
                              {conf.band}{conf.wilson?.length === 2 ? ` (${formatWilson(conf.wilson)})` : ''}
                            </Badge>
                          ) : '—'}
                        </td>
                        <td className="py-2.5 pr-4 text-slate-500">
                          {agg.predicted_distribution ? `${Math.round((agg.predicted_distribution[agg.predicted_winner] ?? 0) * 100)}%` : '—'}
                        </td>
                        <td className="py-2.5 text-right">
                          {isGap && (
                            <Link
                              to={`/app/projects/${projectId}/ask?tab=predict&q=${encodeURIComponent(`${latestPreRead?.research_question ?? ''} — ${agg.segment_name}`)}`}
                              className="text-xs text-echo-indigo hover:underline flex items-center gap-0.5 justify-end"
                            >
                              Explore this gap →
                            </Link>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        ) : (
          <Card className="text-center py-8">
            <p className="text-sm text-slate-400">Pre-read completed but no segment data was returned.</p>
            <Link to={preReadPath} className="text-xs text-human-teal hover:underline mt-2 block">Re-run pre-read →</Link>
          </Card>
        )}

        {/* Confidence overview for first segment */}
        {aggregates[0]?.confidence && (
          <Card>
            <p className="text-xs text-slate-400 uppercase tracking-wide mb-3">Confidence — {aggregates[0].segment_name}</p>
            <ConfidenceMeter confidence={aggregates[0].confidence} groundTruthPresent={false} />
            <p className="text-xs text-slate-400 mt-3">
              No ground truth present for this run. Metrics reflect proxy pass agreement.
            </p>
          </Card>
        )}

        {/* Validation plan CTA */}
        <Card className="border-human-teal/30 bg-human-teal/5">
          <p className="text-xs text-slate-400 uppercase tracking-wide mb-2">Validation Plan</p>
          <p className="text-sm text-slate-700 mb-3">
            Low-confidence segments should be validated with real respondents before finalizing your recommendation.
          </p>
          <Link to={validationPath}>
            <Button variant="primary" size="sm">View validation plan →</Button>
          </Link>
        </Card>

        {/* Caveats */}
        <Card className="bg-soft-panel border-0">
          <p className="text-xs text-slate-400 uppercase tracking-wide mb-2">Caveats</p>
          <div className="text-xs text-slate-500 space-y-1">
            <p>• Confidence reflects Wilson interval over pass agreement at 95%. No ground truth was present.</p>
            <p>• Echosome does not claim causal proof. These are directional signals.</p>
            <p>• Echosome does not replace real research.</p>
          </div>
        </Card>
      </div>
    )
  }

  // ── Demo report ──────────────────────────────────────────────────────────────
  return (
    <div className="p-6 space-y-6 max-w-4xl pb-20 md:pb-6">
      <div className="flex items-center justify-between flex-wrap gap-3 no-print">
        <div>
          <h1 className="text-2xl font-medium text-deep-ink" style={{ letterSpacing: '-0.02em' }}>Client-Ready Report</h1>
          <p className="text-sm text-slate-500 mt-1">
            <Badge variant="demo" size="sm">Demo data</Badge>{' '}
            Insurance Renewal Message Test · Generated {new Date().toLocaleDateString()}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={copyExecSummary}>
            {copied ? <><Check className="inline h-3 w-3 mr-1" />Copied</> : 'Copy summary'}
          </Button>
          <Button variant="outline" size="sm" onClick={() => window.open('/api/projects/demo/report/export', '_blank')}>Export PDF</Button>
          <Link to={dataPath}>
            <Button variant="ghost" size="sm">Download JSON</Button>
          </Link>
        </div>
      </div>

      <div className="space-y-6 print-friendly">
        <Card>
          <p className="text-xs text-slate-400 uppercase tracking-wide mb-2">1. Executive Summary</p>
          <p className="text-sm text-slate-700 leading-relaxed">
            The ensemble pre-read across 1,248 survey responses, 362 open-ended answers, and 18 interview snippets
            predicts that <strong className="text-deep-ink">Message B</strong> will generate the highest trust and renewal intent among General Homeowners and Price-Sensitive Renters.
            The prediction is supported by a consistent pattern across 6 of 7 ensemble passes: respondents respond positively to concrete premium explanations and negatively to vague "market conditions" language.
            Confidence is medium-high overall but low for Recent Claimants — this segment requires human validation before finalizing.
          </p>
        </Card>

        <Card className="bg-human-teal text-white">
          <p className="text-xs text-white/60 uppercase tracking-wide mb-2">2. Decision Recommendation</p>
          <p className="text-xl font-medium">Proceed with Message B as primary renewal message.</p>
          <p className="text-sm text-white/80 mt-1">Validate with Recent Claimants before finalizing claims-acknowledgment language.</p>
        </Card>

        <Card>
          <p className="text-xs text-slate-400 uppercase tracking-wide mb-3">3. Predicted Winner</p>
          <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
            <div>
              <p className="text-2xl font-medium text-human-teal">Message B</p>
              <p className="text-sm text-slate-600 mt-1 max-w-md">"Your renewal premium reflects the actual cost of claims paid in your area last year — we explain exactly what changed and why."</p>
            </div>
            <div className="text-right">
              <Badge variant="success">6 of 7 passes agree</Badge>
              <p className="text-xs text-slate-400 mt-1">~56% predicted preference share</p>
            </div>
          </div>
          <ConfidenceMeter confidence={DEMO_CONFIDENCE} groundTruthPresent={false} />
        </Card>

        <Card>
          <p className="text-xs text-slate-400 uppercase tracking-wide mb-3">4. Why It Wins</p>
          <div className="space-y-3">
            {[
              { reason: 'Concrete explanation reduces anxiety', quote: '"I just want to understand the number. Give me the breakdown and I\'ll accept it."', segment: 'General Homeowners', row: 112 },
              { reason: '"Market conditions" language actively erodes trust', quote: '"Market conditions" is not an explanation. That\'s a non-answer.', segment: 'General Homeowners', row: 203 },
              { reason: 'Transparency language signals respect', quote: '"If they explain exactly what changed and why, I\'m more likely to stay."', segment: 'General Homeowners', row: 47 },
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-3">
                <span className="text-human-teal font-medium shrink-0 mt-0.5">{i + 1}.</span>
                <div>
                  <p className="text-sm font-medium text-deep-ink">{item.reason}</p>
                  <Link to={`${dataPath}?row=${item.row}`} className="text-xs italic text-slate-500 hover:text-human-teal mt-0.5 block">
                    "{item.quote}" — {item.segment}
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card>
          <p className="text-xs text-slate-400 uppercase tracking-wide mb-3">5. Segment Reactions</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border-gray text-xs text-slate-400 text-left">
                  <th className="pb-2 pr-4 font-medium">Segment</th>
                  <th className="pb-2 pr-4 font-medium">Predicted winner</th>
                  <th className="pb-2 pr-4 font-medium">Confidence</th>
                  <th className="pb-2 font-medium">Note</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border-gray">
                {[
                  { seg: 'General Homeowners', winner: 'Message B', conf: 'High', wilson: '72–86%', note: 'Strong evidence base' },
                  { seg: 'Recent Claimants', winner: 'Message B', conf: 'Low', wilson: '32–58%', note: 'Validate — claims language gap' },
                  { seg: 'Price-Sensitive Renters', winner: 'Message B', conf: 'Medium', wilson: '55–73%', note: 'Concrete explanation valued' },
                ].map(row => (
                  <tr key={row.seg}>
                    <td className="py-2.5 pr-4 font-medium">{row.seg}</td>
                    <td className="py-2.5 pr-4 text-human-teal">{row.winner}</td>
                    <td className="py-2.5 pr-4">
                      <Badge variant={row.conf === 'High' ? 'success' : row.conf === 'Medium' ? 'warning' : 'danger'} size="sm">{row.conf} ({row.wilson})</Badge>
                    </td>
                    <td className="py-2.5 text-slate-500 text-xs">{row.note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        <Card>
          <p className="text-xs text-slate-400 uppercase tracking-wide mb-3">6. Top Objections (Skeptical & Counterfactual passes)</p>
          <div className="space-y-2">
            {[
              'Recent claimants may need explicit "your claim matters" framing that none of the three messages provides.',
              'Message B\'s explanation could feel defensive to respondents who expected no increase at all.',
              'Price-Sensitive Renters will still comparison-shop regardless of message — Message B buys goodwill but not retention.',
            ].map((obj, i) => (
              <p key={i} className="text-sm text-slate-600 flex items-start gap-2">
                <AlertTriangle className="h-3.5 w-3.5 text-uncertainty-amber shrink-0 mt-0.5" strokeWidth={1.75} />{obj}
              </p>
            ))}
          </div>
        </Card>

        <Card>
          <p className="text-xs text-slate-400 uppercase tracking-wide mb-3">7. Evidence Quotes</p>
          <div className="space-y-3">
            {[
              { text: '"Appreciate the transparency. Makes me feel like they\'re treating me like an adult."', seg: 'General Homeowners', row: 278, strength: 'High' },
              { text: '"Concrete numbers build trust. I hate vague corporate speak."', seg: 'General Homeowners', row: 341, strength: 'High' },
              { text: '"When I understand why the price went up, I accept it more easily."', seg: 'General Homeowners', row: 489, strength: 'Medium' },
              { text: '"I just went through a stressful claim. I need to know you see me as a person, not a number."', seg: 'Recent Claimants', row: 891, strength: 'Medium' },
            ].map((q, i) => (
              <div key={i} className="bg-soft-panel rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <Badge variant={q.strength === 'High' ? 'success' : 'warning'} size="sm">{q.strength}</Badge>
                  <span className="text-xs text-slate-400">{q.seg} · row {q.row}</span>
                </div>
                <p className="text-sm italic text-slate-600">"{q.text}"</p>
              </div>
            ))}
          </div>
        </Card>

        <Card>
          <p className="text-xs text-slate-400 uppercase tracking-wide mb-3">8. Confidence & Caveats</p>
          <div className="space-y-2 text-sm text-slate-600">
            <p>• Overall confidence is medium-high ({formatWilson(DEMO_CONFIDENCE.wilson)}) based on evidence volume, pass agreement, and source diversity.</p>
            <p>• Confidence for Recent Claimants is Low (Wilson 32–58%) due to thin open-ended evidence in that segment.</p>
            <p>• Diversity is shown separately — it drove the validation plan, not the confidence score.</p>
            <p>• No ground truth was present for this run. Metrics reflect proxy agreement, not validated accuracy.</p>
            <p>• Echosome does not claim causal proof. These are directional signals.</p>
          </div>
        </Card>

        <Card className="border-human-teal/30 bg-human-teal/5">
          <p className="text-xs text-slate-400 uppercase tracking-wide mb-3">9. Validation Plan</p>
          <p className="text-sm text-slate-700 mb-3">
            "Validate Message B with 40 homeowners and 20 recent claimants. Echo confidence is high for general homeowners (Wilson 72–86%) but low for recent claimants (Wilson 32–58%) because prior data has only 12 relevant open-end responses."
          </p>
          <Link to={validationPath}>
            <Button variant="primary" size="sm">Run validation →</Button>
          </Link>
        </Card>

        <Card className="bg-soft-panel border-0">
          <p className="text-xs text-slate-400 uppercase tracking-wide mb-3">Appendix: Method</p>
          <div className="text-xs text-slate-500 space-y-1">
            <p><strong className="text-slate-600">Passes:</strong> Evidence Grounded · Segment Response · Skeptical Researcher · Behavioral Driver · Counterfactual Risk · Quantitative Distribution · Validation Planner</p>
            <p><strong className="text-slate-600">Model:</strong> demo-deterministic (no external LLM in demo mode)</p>
            <p><strong className="text-slate-600">Confidence:</strong> Wilson interval over pass agreement at 95%. Ground truth not present — metrics are proxy agreement.</p>
            <p><strong className="text-slate-600">Evidence:</strong> 1,248 survey rows · 362 open-end responses · 18 interview snippets · 4 prior concept tests</p>
            <p><strong className="text-slate-600">Disclaimer:</strong> Echosome does not claim causal proof. Echosome does not replace real research.</p>
          </div>
        </Card>
      </div>
    </div>
  )
}
